import React from "react";
import moment from "moment";
import Uri from "jsuri";

import { HEADERS } from "components/Utils/Constants";
import { absolute } from "api";
import { getCurrentTenant, selectFormat } from "components/Utils/Utils.js";
import InsightIndicator from "i/brand/pickaxe_small_circle.svg";
import { escapeHtml } from "../../utils/htmlEscape";

const formatMarkup = (type, value, format, highlight) => {
  let style = "";
  let formatted_value = "";

  if (highlight && highlight.name === type) {
    style = `color: ${highlight.color}`;
  }

  switch (type) {
    case "integer":
      formatted_value = selectFormat("integer", value, format || null);
      break;

    case ("time_series", "context", "pith", "metric"):
      formatted_value = value;
      break;

    case "percentage":
      formatted_value = selectFormat("percentage", value, format || null);
      break;

    case "date":
      formatted_value = moment(value).format("MM/DD/YY");
      break;

    case "currency":
      formatted_value = selectFormat("currency", value);
      break;

    case "time":
      const minutes = Math.floor(value / 60);
      formatted_value = selectFormat("integer", minutes) + " minutes";
      break;
    default:
      formatted_value = value;
  }

  return `<span class="insight-token insight-token--${type}" style="${style}">${escapeHtml(formatted_value)}</span>`;
};

const interpolateInsights = (template, markup) => {
  return template.replace(
    // eslint-disable-next-line
    new RegExp("\\${([^{]+)}", "g"),
    (_, name) => markup[name]
  );
};

export const formatInsightText = (text, markup, highlight) => {
  let formattedMarkup = {};
  text.forEach((insight, index) => {
    let obj = Object.keys(insight)[0];
    formattedMarkup[obj] = formatMarkup(
      insight[obj].type,
      insight[obj].value,
      insight[obj].format,
      highlight
    );
  });
  return interpolateInsights(markup, formattedMarkup);
};

export const Loader = (minHeight, size) => {
  return (
    <div className="col-12">
      <div style={{ minHeight: minHeight }} className="loader">
        <div className={`circle-scale ${size}`} />
      </div>
    </div>
  );
};

const showInsights = (e) => {
  const x = Math.round(e.x);

  let insight = e.node.current;
  let windowWidth = window.innerWidth;

  if (e.showIndicator === false) {
    insight.style.opacity = "0";
  } else {
    insight.style.opacity = "1";
  }

  // if we have width it's a bar chart
  // width is the width of the chart's bar
  if (e.width) {
    if (windowWidth <= 991) {
      if (x <= 200) {
        insight.style.transform = `translate(${x + e.width + 5}px, 0px)`;
        insight.classList.remove("carret-top-right");
        insight.classList.add("carret-top-left");
      } else if (x > 201) {
        insight.style.transform = `translate(${x - 203}px, 0px)`;
        insight.classList.remove("carret-top-left");
        insight.classList.add("carret-top-right");
      }
    } else if (windowWidth >= 992) {
      if (x < 240) {
        insight.style.transform = `translate(${x + e.width}px, 5px)`;
        insight.classList.remove("carret-top-right");
        insight.classList.add("carret-top-left");
      } else if (x > 240) {
        insight.style.transform = `translate(${x - 230}px, 5px)`;
        insight.classList.remove("carret-top-left");
        insight.classList.add("carret-top-right");
      }
    }
  } else {
    if (windowWidth <= 991) {
      if (x < 240) {
        insight.style.transform = `translate(${x + 5}px, 0)`;
        insight.classList.remove("carret-top-right");
        insight.classList.add("carret-top-left");
      } else {
        insight.style.transform = `translate(${x - 205}px, 0)`;
        insight.classList.add("carret-top-right");
      }
    } else if (windowWidth >= 992) {
      if (x < 240) {
        insight.style.transform = `translate(${x + 5}px, 5px)`;
        insight.classList.remove("carret-top-right");
        insight.classList.add("carret-top-left");
      } else {
        insight.style.transform = `translate(${x - 235}px, 5px)`;
        insight.classList.add("carret-top-right");
      }
    }
  }
};

export const InsightsWithinTooltip = (props) => {
  const insightsData = props.data;
  let insights = null;

  if (insightsData) {
    if (props.tooltipDate.date) {
      insights = insightsData.find((o) => o.date === props.tooltipDate.date);
    } else {
      insights = insightsData.find((o) => o.date === props.tooltipDate);
    }
  }

  if (insights && insights.data) {
    const insightsCount =
      insights.data.length > 0 ? insights.data.length : false;
    return (
      <>
        <div className="standard-tooltip__dilimiter2 mt-3" />
        <div className="standard-tooltip__section-header mb-3 mt-3">
          Insights
        </div>
        <div className="standard-tooltip__details--posts mb-2 mt-2">
          <div className="d-flex mb-1">
            <img src={InsightIndicator} alt="Pickaxe" width={15} height={15} />
            <div className="standard-tooltip__header-date ml-2">
              {moment(insights.data[0].insight_generation_time).format(
                "MM/DD/YY - hh:mm a"
              )}
            </div>
          </div>
          <div className={"insight__text mb-2"}>
            <span
              dangerouslySetInnerHTML={{
                __html: formatInsightText(
                  insights.data[0].insight_markup_array,
                  insights.data[0].insight_text_markup
                ),
              }}
            />

            {insightsCount && insightsCount > 1 && (
              <div className="insight__more mt-3 mb-3">
                Click on the data series for <b>{insightsCount - 1}</b> more
                insights.
              </div>
            )}
          </div>
        </div>
      </>
    );
  } else {
    return null;
  }
};

export const RenderTooltipInsights = (props) => {
  // Determine if there are any seleted insights at all
  const selected = props.data.find((insights) =>
    insights.data.find((i) => i.selected === true)
  );

  // Find specific insight (we might have more than one insight)
  const selectedInsight =
    selected && selected.data.find((i) => i.selected === true);

  const insightsCount = selected.data.length;
  const showInsightsCount = () => {
    if (insightsCount > 1) {
      return (
        <div className="insights-count mt-2">
          {insightsCount - 1} more insight{insightsCount === 2 ? "" : "s"}{" "}
          available.
        </div>
      );
    }
  };

  if (selectedInsight) {
    return (
      <div id="tooltip-content" className="featured-insight-tooltip-content">
        <div className="standard-tooltip__text" />
        <span
          dangerouslySetInnerHTML={{
            __html: formatInsightText(
              selectedInsight.insight_markup_array,
              selectedInsight.insight_text_markup
            ),
          }}
        />
        {showInsightsCount()}
      </div>
    );
  } else {
    return null;
  }
};

export const OpenSelectedInsight = (node, showIndicator) => {
  if (!showIndicator) {
    node.current.style.opacity = "0";
  } else {
    node.current.style.opacity = "1";
  }
};

export const CloseSelectedInsight = (node) => {
  if (node && node.current) node.current.style.opacity = "0";
};

export const InsightsLineLabel = (props) => {
  const { x, value, insights, height } = props;

  const hasInsight = insights.find((d) => d.date === value);

  if (hasInsight) {
    const isSelected = hasInsight.data.find((i) => i.selected === true);
    const widgetHeight = height;

    return (
      <svg height={widgetHeight}>
        <line
          x1={x}
          y1="10"
          x2={x}
          y2={widgetHeight}
          stroke="#000"
          opacity="0.1"
          strokeWidth={2}
          onLoad={isSelected ? showInsights(props) : null}
        />
      </svg>
    );
  } else {
    return null;
  }
};

export const InsightsTickIndicator = (props) => {
  const { cx = 0, cy, height } = props; // default cx to 0 if not provided
  const style = { overflow: "visible" };

  // Ensure cx is a number. If not, default to 0
  const calculatedX = isNaN(cx) ? 0 : cx - 5;

  // Ensure height is a number and if not, default to cy + 27
  const calculatedY = isNaN(height) ? (isNaN(cy) ? 0 : cy + 27) : height;

  return (
    <svg width={15} height={15} style={style}>
      <image
        x={calculatedX}
        y={calculatedY}
        href={InsightIndicator}
        width="15"
        height="15"
      />
    </svg>
  );
};

const getBackgroundStyles = (i, width, height) => ({
  fill: i === 0 ? "#ececec" : "transparent",
  strokeDasharray: `0 ${width} ${height} 0`,
  strokeWidth: width < 20 && i === 0 ? `3` : width > 20 && i === 0 ? `5` : `0`,
});

export const RenderBackground = (props) => {
  const { insights, x, y, width, height, i } = props;
  let insightDate = props.date;

  if (insights && insights.length > 0) {
    if (typeof props.date === "object") {
      insightDate = props.date.date;
    }

    const hasInsights = insights.find((i) => i.date === insightDate);

    if (hasInsights) {
      const selected = hasInsights.data.find((i) => i.selected === true);

      return (
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          style={getBackgroundStyles(i, width, height)}
          stroke="#ececec"
          onLoad={
            selected && selected !== undefined
              ? showInsights(props, x, y, width)
              : null
          }
        />
      );
    } else {
      return null;
    }
  } else {
    return null;
  }
};

/**
 * Enrich widget's data with insights
 *
 *   @param {object} data - widget's data
 *   @param {integer} project - project's name
 *   @param {interget} project_id - project's id
 * * @param {object} insightsConfig - project's insights config
 */
export const fetchInsights = async (
  data,
  project,
  project_id,
  insightsConfig,
  board
) => {
  if (data.insights_query) {
    const tenant = getCurrentTenant(project);
    const version = window.localStorage.configVersion || "production";

    const threshold_param = new Uri(window.location.search).getQueryParamValue(
      "threshold"
    );

    const threshold_config = new Uri(
      insightsConfig.widgets.url
    ).getQueryParamValue("threshold");

    // Set appropriate dashboard version and threshold
    data.insights_query.version = version;
    data.insights_query.threshold = threshold_param || threshold_config;

    const endpoint = `/dashboard/${tenant}/query-insights-widget`;
    const params = JSON.stringify(data.insights_query);

    // Add board parameter to URL if it exists
    const boardParam = board ? `&board=${board}` : "";
    const url = `${endpoint}?project_id=${project_id}${boardParam}&query=${params}`;

    let response = await fetch(absolute(url), {
      method: "GET",
      headers: HEADERS(window.localStorage.token),
    });

    if (!response.ok) {
      console.log("Error Loading Insights");
    } else {
      let data = await response.json();
      return data.insights;
    }
  }
};

export const closeAllLinechartTooltips = () => {
  document
    .querySelectorAll(".xray-tooltip__close-icon")
    .forEach((el) => el.click());
};

export const closeAllBarchartTooltips = () => {
  document
    .querySelectorAll(".xray-tooltip__close-icon-barchart")
    .forEach((el) => el.click());
};
